import React, { Component } from 'react';
import {
  LinearProgress, Box, Card, CardContent, Typography, Divider, FormControl,
  InputLabel, Select, MenuItem, Chip, Stack, Avatar, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import {
  ResponsiveContainer,
  BarChart as RBarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import * as ETVConstants from '../ETVConstants';

const APP_FONT = '"IBM Plex Sans", Roboto, Arial, sans-serif';
const ALL = '__ALL__';
const fmtInt = new Intl.NumberFormat('en-US');
const fmtPct = (v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);

// Helpers for thousands view in chart & KPI
const toK = (n) => (+n || 0) / 1000;
const fmtK = (n) => `${toK(n).toFixed(1)}K`;

const COLORS = {
  impressions: { fg: '#1976d2', bg: 'rgba(25,118,210,0.08)' },
  downloads:   { fg: '#2e7d32', bg: 'rgba(46,125,50,0.10)'  },
  redeemed:    { fg: '#8e24aa', bg: 'rgba(142,36,170,0.10)' },
};

class VoucherStatistics extends Component {
  state = {
    statistics_data: {},
    isLoading: false,
    isLoaded: false,
    selectedDevice: ALL,
    selectedLocation: ALL,
    error: null,
  };

  componentDidMount() {
    if (this.props.voucher_def_id && !this.state.isLoading && !this.state.isLoaded) {
      this.loadData();
    }
  }
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.voucher_def_id !== this.props.voucher_def_id && this.props.voucher_def_id) {
      this.loadData();
      return;
    }
    if (
      prevState.selectedLocation !== this.state.selectedLocation ||
      prevState.statistics_data !== this.state.statistics_data
    ) {
      const deviceOptions = this.getDeviceOptions(this.state.statistics_data, this.state.selectedLocation);
      const allowedIds = new Set(deviceOptions.map(d => d.id));
      if (!allowedIds.has(this.state.selectedDevice)) {
        this.setState({ selectedDevice: ALL });
      }
    }
  }

  loadData = () => {
    this.setState({ isLoading: true, isLoaded: false, error: null });
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');
    const url = `${ETVConstants.getServerBase()}/voucher_mgmt?action=statistics3&voucher_def_id=${this.props.voucher_def_id}`;
    fetch(url, { headers: { 'user_id': reqUserID, 'access_token': accessToken } })
      .then(res => res.json())
      .then(
        (result) => this.setState({ statistics_data: result || {}, isLoading: false, isLoaded: true }),
        (error)  => this.setState({ statistics_data: {}, isLoading: false, isLoaded: true, error: error?.message || 'Failed to load' })
      );
  };

  // ---- helpers ----
  getDeviceOptions = (data, selectedLocation) => {
    const byDevice = new Map();
    const take = (arr) => (arr || []).forEach(x => {
      if (!x?.device_id) return;
      const name = x.device_name?.trim() ? x.device_name : x.device_id;
      if (!byDevice.has(x.device_id)) byDevice.set(x.device_id, { name, locs: new Set() });
      byDevice.get(x.device_id).locs.add(x.location_id ?? null);
    });
    take(data.impressions); take(data.downloads); take(data.redemptions);
    (data?.summary?.devices || []).forEach(id => {
      if (!byDevice.has(id)) byDevice.set(id, { name: id, locs: new Set([null]) });
    });

    const list = [];
    byDevice.forEach((val, id) => {
      if (selectedLocation === ALL || val.locs.has(selectedLocation)) list.push({ id, name: val.name });
    });
    list.sort((a, b) => a.name.localeCompare(b.name));
    return [{ id: ALL, name: 'All devices' }, ...list];
  };

  getLocations = (data) => {
    const map = new Map();
    const take = (arr) => (arr || []).forEach(x => {
      if (x && x.location_id != null) map.set(x.location_id, x.location_name || `Loc ${x.location_id}`);
    });
    take(data.impressions); take(data.downloads); take(data.redemptions);
    (data?.summary?.locations || []).forEach(l => {
      if (l && l.location_id != null && !map.has(l.location_id)) map.set(l.location_id, l.location_name || `Loc ${l.location_id}`);
    });
    const out = Array.from(map, ([id, name]) => ({ id, name }));
    out.sort((a, b) => (String(a.name)).localeCompare(String(b.name)));
    return [{ id: ALL, name: 'All locations' }, ...out];
  };

  // Build series; keep imprK for chart balance; device/location filters applied
  buildSeries = (data, selectedDevice, selectedLocation) => {
    const seriesMap = new Map();
    const fit = (rec) => (selectedDevice === ALL || rec.device_id === selectedDevice) &&
                         (selectedLocation === ALL || rec.location_id === selectedLocation);
    const add = (arr, key, field) => (arr || []).forEach(rec => {
      if (!fit(rec)) return;
      const date = rec.date;
      if (!seriesMap.has(date)) seriesMap.set(date, { date, impressions: 0, imprK: 0, downloads: 0, redeemed: 0 });
      const v = Number(rec[field] || 0);
      if (key === 'impressions') {
        seriesMap.get(date).impressions += v;
        seriesMap.get(date).imprK += v / 1000;
      } else {
        seriesMap.get(date)[key] += v;
      }
    });
    add(data.impressions, 'impressions', 'impressions');
    add(data.downloads,   'downloads',   'downloads');
    add(data.redemptions, 'redeemed',    'redeemed');
    return Array.from(seriesMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  };

  buildPerDevice = (data, selectedLocation) => {
    const agg = new Map();
    const fitLoc = (rec) => selectedLocation === ALL || rec.location_id === selectedLocation;
    const touch = (id, name, locId, locName) => {
      if (!agg.has(id)) {
        agg.set(id, {
          device_id: id,
          device_name: name || id,
          location_id: locId ?? null,
          location_name: locName || (locId != null ? `Loc ${locId}` : 'Unknown'),
          impressions: 0, downloads: 0, redeemed: 0,
        });
      }
    };
    (data.impressions || []).forEach(i => { if (fitLoc(i)) { touch(i.device_id, i.device_name, i.location_id, i.location_name); agg.get(i.device_id).impressions += +i.impressions || 0; }});
    (data.downloads   || []).forEach(d => { if (fitLoc(d)) { touch(d.device_id, d.device_name, d.location_id, d.location_name); agg.get(d.device_id).downloads   += +d.downloads   || 0; }});
    (data.redemptions || []).forEach(r => { if (fitLoc(r)) { touch(r.device_id, r.device_name, r.location_id, r.location_name); agg.get(r.device_id).redeemed    += +r.redeemed    || 0; }});
    const rows = Array.from(agg.values());
    rows.forEach(r => { r.downloadRate = r.impressions > 0 ? (r.downloads / r.impressions) : null;
                        r.redeemRate   = r.downloads   > 0 ? (r.redeemed  / r.downloads)   : null; });
    rows.sort((a, b) => (b.downloads - a.downloads) || (b.redeemed - a.redeemed));
    return rows;
  };

    // KPI (impressions shows value/1000 without "K")
  // Accepts icon either as a React element (<Icon />) or as a component (Icon)
  KpiCard = ({ color = COLORS.impressions, icon, label = '', value = 0, helper, formatNumber }) => {
    const safeValue = Number.isFinite(+value) ? +value : 0;

    const renderIcon = () => {
      if (!icon) return null;
      // If it's already a valid element, clone to add sizing
      if (React.isValidElement(icon)) {
        return React.cloneElement(icon, { sx: { fontSize: 28 } });
      }
      // If it's a component (function/class), instantiate it
      if (typeof icon === 'function') {
        const IconComp = icon;
        return <IconComp sx={{ fontSize: 28 }} />;
      }
      return null;
      // Note: if you pass a string or object here, it will return null instead of crashing
    };

    return (
      <Card
        elevation={1}
        sx={{
          height: '100%',
          borderRadius: 2,
          backgroundColor: color?.bg || 'background.paper',
          border: `1px solid ${(color?.fg || '#000')}22`,
          width: '100%',
        }}
      >
        <CardContent
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            alignItems: 'center',
            gap: 2,
            minHeight: 120,
            py: 2,
          }}
        >
          <Box sx={{ minHeight: 72, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1, fontFamily: APP_FONT }}>
              {String(label || '').toUpperCase()}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontWeight: 700,
                color: color?.fg || 'text.primary',
                fontFamily: '"Roboto Mono", monospace',
                fontSize: 42,
                lineHeight: 1,
              }}
            >
              {formatNumber ? formatNumber(safeValue) : Intl.NumberFormat('en-US').format(safeValue)}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                color: 'text.secondary',
                fontFamily: APP_FONT,
                fontSize: 20,
                fontWeight: 400,
                visibility: helper ? 'visible' : 'hidden',
              }}
            >
              {helper || 'placeholder'}
            </Typography>
          </Box>

          <Avatar sx={{ bgcolor: color?.fg || 'primary.main', color: '#fff', width: 56, height: 56, boxShadow: 1 }}>
            {renderIcon()}
          </Avatar>
        </CardContent>
      </Card>
    );
  };


  render() {
    const { isLoading, statistics_data, selectedDevice, selectedLocation, error } = this.state;
    if (isLoading) return <LinearProgress />;
    if (error) return <Typography color="error" sx={{ p: 2 }}>{error}</Typography>;
    if (!statistics_data || Object.keys(statistics_data).length === 0) return <Typography sx={{ p: 2 }}>No data.</Typography>;

    const data = statistics_data;
    const locations = this.getLocations(data);
    const deviceOptions = this.getDeviceOptions(data, selectedLocation);
    const series = this.buildSeries(data, selectedDevice, selectedLocation);
    const perDevice = this.buildPerDevice(data, selectedLocation);

    const totalImpr = data?.summary?.total_impressions || 0;
    const totalDl   = data?.summary?.total_downloads   || 0;
    const totalRed  = data?.summary?.total_redeemed    || 0;
    const dlRateStr = totalImpr > 0 ? fmtPct(totalDl / totalImpr) : '—';
    const redRateStr= totalDl   > 0 ? fmtPct(totalRed / totalDl)  : '—';

    const voucherTitle = data?.voucher?.title || `Voucher #${this.props.voucher_def_id}`;
    const dateFrom = data?.date_from || '';
    const dateTo   = data?.date_to   || '';
    const contentLinked = (data?.voucher?.content_reference ?? -1) > 0;

    // Recharts tooltip: Impressions in K, others raw
    const tooltipFormatter = (value, name, props) => {
      if (props?.dataKey === 'imprK') return [`${Number(value).toFixed(1)}K`, 'Impressions (K)'];
      return [fmtInt.format(value), name];
    };

    return (
      <Box sx={{ p: 2, fontFamily: APP_FONT }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontFamily: APP_FONT }}>{voucherTitle}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: APP_FONT }}>
            {dateFrom && dateTo ? `(${dateFrom} — ${dateTo})` : ''}
          </Typography>
          {!contentLinked && <Chip size="small" color="warning" label="No content linked → no impressions" />}
        </Stack>

        {/* KPI + Filters */}
        <Stack
          direction="row"
          spacing={1.25}
          sx={{
            mb: 2,
            alignItems: 'stretch',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            '& > .kpiCell': {
              display: 'flex',
              flexGrow: 0,
              flexBasis: { xs: '100%', sm: 'calc(50% - 10px)', md: '21%' },
            },
            '& > .filterCell': {
              display: 'flex',
              flexGrow: 0,
              flexBasis: { xs: '100%', sm: '100%', md: '33%' },
            },
          }}
        >
          <Box className="kpiCell">
            {/* KPI shows impressions */}
            <this.KpiCard
              color={COLORS.impressions}
              icon={<VisibilityOutlinedIcon />}
              label="impressions"
              value={totalImpr}
            />
          </Box>
          <Box className="kpiCell">
            <this.KpiCard
              color={COLORS.downloads}
              icon={<DownloadOutlinedIcon />}
              label="QR Scans"
              value={totalDl}
              helper={`Scan rate: ${dlRateStr}`}
            />
          </Box>
          <Box className="kpiCell">
            <this.KpiCard
              color={COLORS.redeemed}
              icon={<RedeemOutlinedIcon />}
              label="Redeemed"
              value={totalRed}
              helper={`Redeem rate: ${redRateStr}`}
            />
          </Box>

          <Box className="filterCell">
            <Card elevation={1} sx={{ height: '100%', borderRadius: 2, width: '100%' }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary" sx={{ fontFamily: APP_FONT }}>Filters</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="device-select-label">Device</InputLabel>
                    <Select
                      labelId="device-select-label"
                      value={selectedDevice}
                      label="Device"
                      onChange={(e) => this.setState({ selectedDevice: e.target.value })}
                    >
                      {deviceOptions.map(opt => (<MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="location-select-label">Location</InputLabel>
                    <Select
                      labelId="location-select-label"
                      value={selectedLocation}
                      label="Location"
                      onChange={(e) => this.setState({ selectedLocation: e.target.value })}
                    >
                      {this.getLocations(data).map(l => (<MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Stack>

        {/* Chart — always bars; impressions in K for balance */}
        <Card elevation={1} sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1, fontFamily: APP_FONT }}>{ETVConstants.trans("daily_trend")}</Typography>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ height: 360, '& *': { fontFamily: APP_FONT } }}>
              <ResponsiveContainer width="100%" height="100%">
                <RBarChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals tick={{ fontSize: 12 }} />
                  <Tooltip formatter={tooltipFormatter} contentStyle={{ fontFamily: APP_FONT, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontFamily: APP_FONT, fontSize: 12 }} />
                  <Bar dataKey="imprK"     name="Impressions (K)" fill={COLORS.impressions.fg} />
                  <Bar dataKey="downloads" name="QR Scans"        fill={COLORS.downloads.fg} />
                  <Bar dataKey="redeemed"  name="Redeemed"        fill={COLORS.redeemed.fg} />
                </RBarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        {/* Per-device table */}
        <Card elevation={1} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1, fontFamily: APP_FONT }}>Per device</Typography>
            <Divider sx={{ mb: 1 }} />
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ '& th, & td': { fontFamily: APP_FONT } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{ETVConstants.trans("screen")}</TableCell>
                    <TableCell>{ETVConstants.trans("location")}</TableCell>
                    <TableCell align="right">{ETVConstants.trans("impressions")}</TableCell>
                    <TableCell align="right">{ETVConstants.trans("scans")}</TableCell>
                    <TableCell align="right">{ETVConstants.trans("redeemed")}</TableCell>
                    <TableCell align="right">{ETVConstants.trans("scan_rate")}</TableCell>
                    <TableCell align="right">{ETVConstants.trans("redeem_rate")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {perDevice.map(r => (
                    <TableRow key={r.device_id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ fontFamily: APP_FONT }}>{r.device_name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{r.location_name}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: '"Roboto Mono", monospace' }}>{fmtInt.format(r.impressions)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: '"Roboto Mono", monospace' }}>{fmtInt.format(r.downloads)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: '"Roboto Mono", monospace' }}>{fmtInt.format(r.redeemed)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: '"Roboto Mono", monospace' }}>{fmtPct(r.downloadRate)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: '"Roboto Mono", monospace' }}>{fmtPct(r.redeemRate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  }
}

export default VoucherStatistics;
